import qs from 'qs'
import type { FeatureCollection } from 'geojson'
import { useNuxtApp, useRoute } from 'nuxt/app'
import type { ShowResponse } from '../types/Api/Response/ShowResponse'
import type { Pagination } from '../types/Api/Query/Pagination'
import type { Sort } from '../types/Api/Query/Sort'
import type { IndexResponse } from '../types/Api/Response/IndexResponse'
import type { ShowMeta } from '../types/Api/Response/ShowMeta'
import { FormService } from '../services/form.service'
import type { Rule } from '../types/Api/Rule'
import type { ComponentType } from '../types/componentType.type'

export abstract class BaseModel<T> { // BaseModel<T> extended by entity model (APPS/NUXT_VOIRIE/models/Intention.ts)
    endPoint: string = null
    translationKey: string = ''
    permissionName: string = null
    data: T | null = null
    meta: ShowMeta | null = null
    readonly $route: any
    loaded: boolean = false
    readonly $api: any = null
    parentEndPoint: string | null = null
    currentKey: string | number | null = null

    constructor(data: T | null = null, meta: ShowMeta | null = null, parentEndPoint: string | null = null, currentKey: string | number | null = null) {
        const { $api } = useNuxtApp()
        this.$api = $api
        this.$route = useRoute()
        this.data = data
        this.meta = meta
        this.parentEndPoint = parentEndPoint
        this.currentKey = currentKey
    }

    getRules(): Record<string, string[]> {
        const res: Record<string, string[]> = {}
        if (!this.meta?.rules)
            return res

        Object.keys(this.meta.rules).forEach((rule) => {
            res[rule] = this._splitLaravelRuleString(this.meta.rules[rule])
        })
        return res
    }

    _splitLaravelRuleString(ruleString: string): string[] {
        return ruleString.match(/regex:\/.*?\/[a-z]*|[^|]+/g) || []
    }

    getRulesObjects(): { [key: string]: { [key: string]: Rule } } {
        const res: { [key: string]: { [key: string]: Rule } } = {}
        if (!this.meta?.rules)
            return res

        Object.keys(this.meta.rules).forEach((property) => {
            res[property] = this.getRulesForProperty(property)
        })
        return res
    }

    getRulesForProperty(property: string): { [key: string]: Rule } {
        const res: { [key: string]: Rule } = {}
        const rulesString: string | null = this.meta?.rules[property]
        if (rulesString) {
            rulesString.split('|').forEach((ruleString: string) => {
                const name = ruleString.split(':')[0]
                const param_string = ruleString.split(':')[1]
                const params = {}
                param_string?.split(',').map((t: string) => {
                    const split = t.split('=')
                    params[split[0]] = split[1] ?? true
                })
                res[name] = { property, name, param_string, params } as Rule
            })
        }

        return res
    }

    getInputComponentType(property: string): ComponentType | null { // to tpe returned values
        const rules = this.getRulesForProperty(property)

        if (rules.input_type) {
            return rules.input_type.param_string as ComponentType
        }

        if (rules?.string && rules?.in)
            return 'select'

        if (rules?.array) {
            const nestedKeyPrefix = `${property}.*.`
            const nestedKeys = Object.keys(this.meta.rules).filter(key => key.startsWith(nestedKeyPrefix))
            for (const nestedKey of nestedKeys) {
                // const nestedRules = this.meta.rules[nestedKey]
                const nestedRules = this.getRulesForProperty(nestedKey)
                for (const k of ['string', 'date', 'boolean', 'image', 'file', 'email', 'regex', 'hex_color']) {
                    if (nestedRules[k]) {
                        return k as ComponentType
                    }
                }
            }
        }

        for (const k of ['string', 'date', 'boolean', 'image', 'file', 'email', 'regex', 'hex_color', 'array']) {
            if (rules[k]) {
                return k as ComponentType
            }
        }

        if (rules.integer || rules.numeric) {
            return 'number'
        }

        if (rules.autocompleteUrl) {
            return 'autocomplete'
        }

        return null
    }

    getEditableDataFormat(): Record<string, string[]> {
        const canBeUpdatedData = {}
        Object.keys(this.meta.rules).forEach((ruleKey) => {
            // HANDLE AUTOCOMPLETE _ID
            if (this.meta.rules[ruleKey].includes('autocompleteUrl:') && ruleKey.includes('_id')) {
                const newKey = ruleKey.replace('_id', '')
                canBeUpdatedData[ruleKey] = this.data[newKey]?.id !== undefined ? this.data[newKey]?.id : null
                canBeUpdatedData[newKey] = this.data[newKey]?.id !== undefined ? this.data[newKey] : null // needed to get full data from form.service for autocomplete
            }
            else {
                canBeUpdatedData[ruleKey] = this.data[ruleKey] !== undefined ? this.data[ruleKey] : null
            }
        })
        return canBeUpdatedData
    }

    getEndPoint(useEntityId: boolean = false): string {
        const baseEndPoint = this.parentEndPoint ? `${this.parentEndPoint}/${this.endPoint}` : this.endPoint
        if (useEntityId)
            return `${baseEndPoint}/${this.getKey()}`
        return baseEndPoint
    }

    setKey(newKey: number | string | null): void {
        this.currentKey = newKey
    }

    getKey(): string | null {
        return (this.data?.k ?? this.currentKey) ?? null
    }

    getTranslationKey(): string {
        return this.translationKey
    }

    // noinspection JSUnusedGlobalSymbols
    getPermissionName(): string {
        return this.permissionName
    }

    // TODO decide how to handle Translation on entity in Generic
    setTranslationKey(newTranslationKey: string): void {
        this.translationKey = newTranslationKey
    }

    async put(data: { [key: string]: any }): Promise<BaseModel<T> | null> {
        if (!this.getKey())
            return null
        // console.log('track entityPut')

        return new Promise<BaseModel<T> | null>((resolve) => {
            this.$api.setRequest({
                url: this.getEndPoint(true),
                body: data,
                method: 'PUT',
                translation: this.getTranslationKey(),
            }).then((value: ShowResponse<T>) => {
                resolve(value ? this.newStatic(value.data, value.meta) : null)
                // TODO test validation errors and not found
            })
        })
    }

    post(data: { [key: string]: any }, showToaster: boolean = true): Promise<BaseModel<T> | null> {
        if (this.getKey())
            return null

        return new Promise<BaseModel<T> | null>((resolve) => {
            this.$api.setRequest({
                url: `${this.getEndPoint()}`,
                body: data,
                method: 'POST',
                translation: showToaster ? this.getTranslationKey() : undefined,
            }).then((value: ShowResponse<T>) => {
                resolve(value ? this.newStatic(value.data, value.meta) : null)
                // TODO test validation errors
            })
        })
    }

    createNewEntity(data: { [key: string]: any }): Promise<BaseModel<T> | null> {
        return new Promise<BaseModel<T> | null>((resolve) => {
            this.$api.setRequest({
                url: `${this.getEndPoint()}`,
                body: data,
                method: 'POST',
                translation: this.getTranslationKey(),
            }).then((value: ShowResponse<T>) => {
                resolve(value ? this.newStatic(value.data, value.meta) : null)
                // TODO test validation errors
            })
        })
    }

    get(key: string | number | null = null): Promise<BaseModel<T> | null> {
        return new Promise<BaseModel<T> | null>((resolve) => {
            const url = key ? `${this.getEndPoint()}/${key}` : `${this.getEndPoint(true)}`
            this.$api.setRequest({
                url,
                method: 'GET',
                // translation: this.getTranslationKey(), // no toaster on get
            }).then((value: ShowResponse<T>) => {
                resolve(value ? this.newStatic(value.data, value.meta) : null)
                // TODO test no found
            })
        })
    }

    getCreate(query: {
        [key: string]: any
    } = {}): Promise<BaseModel<T> | null> {
        return new Promise<BaseModel<T> | null>((resolve) => {
            let url = `${this.getEndPoint()}/create`
            if (Object.keys(query).length)
                url = `${url}?${qs.stringify(query)}`

            this.$api.setRequest({
                url,
                method: 'GET',
                // translation: this.getTranslationKey(), no toaster on get create
            }).then((value: ShowResponse<T>) => {
                resolve(value ? this.newStatic(value.data, value.meta) : null)
            })
        })
    }

    delete(): Promise<BaseModel<T> | void> {
        return new Promise<BaseModel<T> | void>((resolve) => {
            this.$api.setRequest({
                url: `${this.getEndPoint(true)}`,
                method: 'DELETE',
                translation: this.getTranslationKey(),
            }).then(() => {
                resolve()
                // TODO test no found
            })
        })
    }

    indexData(pagination: Pagination, sort: Sort = null, query: {
        [key: string]: any
    } = {}): Promise<IndexResponse<T>> {
        return new Promise<IndexResponse<T>>((resolve) => {
            const url = this.getEndPoint()
            this.$api.getListData({
                url,
                method: 'GET',
                translation: this.getTranslationKey(),
                pagination,
                sort,
                query,
            }).then((value: IndexResponse<T>) => {
                resolve(value)
            })
        })
    }

    indexPositionsData(sort: Sort = null, query: any[] = []): Promise<T[] | string[]> {
        return new Promise<T[] | string[]>((resolve) => {
            const url = `${this.getEndPoint()}?${qs.stringify({ format: 'position' })}`
            this.$api.getListData({
                url,
                method: 'GET',
                translation: this.getTranslationKey(),
                sort,
                query,
            }).then((value: IndexResponse<T>) => {
                resolve(value.data)
            })
        })
    }

    getGeojson(query: any[] = []): Promise<FeatureCollection> {
        return new Promise<any>((resolve) => {
            const url = `${this.getEndPoint()}?${qs.stringify({ format: 'geojson' })}`
            this.$api.getListData({
                url,
                method: 'GET',
                translation: this.getTranslationKey(),
                query,
            }).then((value: FeatureCollection) => {
                resolve(value)
            })
        })
    }

    newStatic(data: T | object | null = null, meta: ShowMeta | null = null): this {
        return new this.constructor(data, meta, this.parentEndPoint)
    }

    protected getFormService(): FormService {
        return new FormService(this)
    }

    // noinspection SpellCheckingInspection
    getVuelidations(): object {
        return this.getFormService().getVuelidateRules(this.getRules())
    }
}
